import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, RotateCcw } from "lucide-react";
import { adminRequest } from "lib/adminSiteApi";
import lightPhoto from "styles/ios-wallpaper-light.jpg";
import darkPhoto from "styles/ios-wallpaper-dark.jpg";

const THEMES = [["light", "Light mode"], ["dark", "Dark mode"]];
const BUILT_IN = { light: lightPhoto, dark: darkPhoto };
const MAX_BYTES = 640 * 1024;

// Wallpaper behind the glass, per theme: a staff member's own picture, else the default for
// everyone, else the built-in Magellan photo (in the stylesheet). Pictures arrive as blobs and
// are set as --wp-light / --wp-dark on the app root (the site CSP allows blob: images).
export function useWallpaper({ staff = false } = {}) {
  const [tick, setTick] = useState(0);
  const [state, setState] = useState({ info: null, urls: {} });
  const made = useRef([]);
  const refresh = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    let alive = true;
    const urls = {}, created = [];
    (async () => {
      const info = staff ? (await adminRequest("/admin/appearance"))?.data || null : null;
      // Each picture loads on its own; one that fails falls back without blocking the other.
      const picture = async (theme) => {
        if (staff && info?.mine?.[theme]) {
          try { return (await adminRequest(`/admin/appearance/mine/${theme}`, { blob: true })).data; } catch { /* Falls back to the default. */ }
        }
        if (staff && !info?.defaults?.[theme]) return null;
        try {
          const r = await fetch(`/api/site/v1/appearance/wallpaper/${theme}`, { credentials: "omit", cache: "no-cache" });
          return r.ok && /^image\//.test(r.headers.get("content-type") || "") ? await r.blob() : null;
        } catch {
          return null;
        }
      };
      for (const [theme] of THEMES) {
        const blob = await picture(theme);
        if (blob) { urls[theme] = URL.createObjectURL(blob); created.push(urls[theme]); }
      }
      if (!alive) { created.forEach((u) => URL.revokeObjectURL(u)); return; }
      made.current.forEach((u) => URL.revokeObjectURL(u));
      made.current = created;
      setState({ info, urls });
    })().catch(() => { created.forEach((u) => URL.revokeObjectURL(u)); });
    return () => { alive = false; };
  }, [staff, tick]);
  useEffect(() => () => made.current.forEach((u) => URL.revokeObjectURL(u)), []);
  const style = {};
  if (state.urls.light) style["--wp-light"] = `url("${state.urls.light}")`;
  if (state.urls.dark) style["--wp-dark"] = `url("${state.urls.dark}")`;
  return { ...state, style, refresh };
}

// Resizes any picture the browser can open into a JPEG small enough to store (≤ 640 KB).
export async function toWallpaperJpeg(file) {
  if (!file || !/^image\//.test(file.type || "")) throw new Error("Choose a picture: JPEG, PNG or WebP.");
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("This picture could not be opened. Try a JPEG or PNG."));
      i.src = url;
    });
    for (const [edge, quality] of [[2560, 0.84], [2200, 0.78], [1920, 0.72], [1600, 0.66]]) {
      const scale = Math.min(1, edge / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
      if (blob && blob.size <= MAX_BYTES) return blob;
    }
    throw new Error("This picture is too large, even after resizing.");
  } finally {
    URL.revokeObjectURL(url);
  }
}

const when = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");

function Row({ label, picture, note, busy, onPick, onReset, canReset, resetLabel }) {
  const input = useRef(null);
  return (
    <li className="ios-row ios-wallpaper-row">
      <span className="ios-wallpaper-thumb" style={{ backgroundImage: `url("${picture}")` }} aria-hidden="true" />
      <span className="ios-row-main"><span className="ios-row-title">{label}</span><span className="ios-row-sub">{note}</span></span>
      <input ref={input} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onPick(f); }} />
      <button type="button" className="ios-btn ios-btn-gray ios-icon-btn" aria-label={`Upload a picture for ${label.toLowerCase()}`} title="Upload a picture" disabled={busy} onClick={() => input.current?.click()}>
        <ImagePlus size={16} aria-hidden="true" />
      </button>
      {canReset && (
        <button type="button" className="ios-btn ios-btn-gray ios-icon-btn" aria-label={resetLabel} title={resetLabel} disabled={busy} onClick={onReset}>
          <RotateCcw size={16} aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

// Settings section: your own wallpaper, and (with settings rights) the default for everyone.
export function WallpaperSettings({ wallpaper }) {
  const { info, urls, refresh } = wallpaper;
  const [busy, setBusy] = useState(""), [error, setError] = useState(""), [done, setDone] = useState("");
  const save = async (scope, theme, file) => {
    setBusy(scope + theme);
    setError("");
    setDone("");
    try {
      const body = file ? await toWallpaperJpeg(file) : new Blob([]);
      await adminRequest(`/admin/appearance/${scope}/${theme}`, { method: "PUT", binary: true, body });
      setDone(scope === "default" ? "Saved for everyone." : "Saved for you.");
      refresh();
    } catch (e) {
      setError(e?.code === "image_too_large" ? "That picture is too large." : e?.message && !e.code ? e.message : "The wallpaper could not be saved. Try again.");
    } finally {
      setBusy("");
    }
  };
  return (
    <>
      <h3 className="ios-group-header"><span>Your wallpaper</span><span>Only you see this</span></h3>
      <ul className="ios-group">
        {THEMES.map(([theme, label]) => (
          <Row key={theme} label={label} picture={urls[theme] || BUILT_IN[theme]} busy={Boolean(busy)}
            note={info?.mine?.[theme] ? `Your picture · ${when(info.mine[theme].updatedAt)}` : info?.defaults?.[theme] ? "Default for everyone" : "Magellan photo"}
            onPick={(f) => save("mine", theme, f)} canReset={Boolean(info?.mine?.[theme])} resetLabel={`Use the default for ${label.toLowerCase()}`} onReset={() => save("mine", theme, null)} />
        ))}
      </ul>
      {info?.canManageDefaults && (
        <>
          <h3 className="ios-group-header"><span>Default for everyone</span><span>Staff and customers</span></h3>
          <ul className="ios-group">
            {THEMES.map(([theme, label]) => (
              <Row key={theme} label={label} picture={info.defaults?.[theme] && !info.mine?.[theme] && urls[theme] ? urls[theme] : BUILT_IN[theme]} busy={Boolean(busy)}
                note={info.defaults?.[theme] ? `Custom picture · ${when(info.defaults[theme].updatedAt)}` : "Magellan photo"}
                onPick={(f) => save("default", theme, f)} canReset={Boolean(info.defaults?.[theme])} resetLabel={`Go back to the Magellan photo for ${label.toLowerCase()}`} onReset={() => save("default", theme, null)} />
            ))}
          </ul>
        </>
      )}
      <p className="ios-group-footer" role="status">{busy ? "Saving…" : error || done || "Pictures are resized in your browser before upload."}</p>
    </>
  );
}
